/* eslint-disable consistent-return */
import { useState, useCallback } from 'react';

import api from '../services/api';

/**
 * Hook customizado para fetch de dados com estados de loading, error e dados
 * @param {Object} options - Opções de configuração
 * @param {Function} options.onSuccess - Callback executado em caso de sucesso
 * @param {Function} options.onError - Callback executado em caso de erro
 * @param {string} options.defaultErrorMessage - Mensagem de erro padrão
 * @returns {Object} Objeto com dados, loading, error e função fetchData
 */
export const useFetchData = (options = {}) => {
  const {
    onSuccess,
    onError,
    defaultErrorMessage = 'Erro ao carregar dados',
  } = options;

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchData = useCallback(async (query, additionalOptions = {}) => {
    if (!query) {
      console.warn('useFetchData: query é obrigatório');
      return;
    }

    const {
      onSuccess: customOnSuccess,
      onError: customOnError,
      showLoading = true,
      clearData = true,
      contextData, // Dados adicionais para passar aos callbacks
    } = additionalOptions;

    try {
      if (showLoading) {
        setLoading(true);
      }

      if (clearData) {
        setData([]);
      }

      setError(null);

      const response = await api.get(query);
      const responseData = response.data;

      // Ajusta para diferentes estruturas de resposta
      let processedData = [];

      if (responseData.docs) {
        processedData = responseData.docs;
      } else if (responseData.data) {
        processedData = responseData.data;
      } else if (Array.isArray(responseData)) {
        processedData = responseData;
      } else {
        processedData = [];
      }

      setData(processedData);
      setLoading(false);

      // Executa callbacks de sucesso
      if (customOnSuccess) {
        customOnSuccess(processedData, response, contextData);
      } else if (onSuccess) {
        onSuccess(processedData, response, contextData);
      }

      return { data: processedData, response };
    } catch (err) {
      const errorMessage = err?.response?.data?.message || defaultErrorMessage;
      setError(errorMessage);
      setLoading(false);

      console.error('useFetchData Error:', err);

      // Executa callbacks de erro
      if (customOnError) {
        customOnError(err, errorMessage, contextData);
      } else if (onError) {
        onError(err, errorMessage, contextData);
      }

      throw err;
    }
  }, [onSuccess, onError, defaultErrorMessage]);

  const clearData = useCallback(() => {
    setData([]);
    setError(null);
    setLoading(false);
  }, []);

  const setDataManually = useCallback((newData) => {
    setData(newData);
  }, []);

  return {
    data,
    loading,
    error,
    fetchData,
    clearData,
    setData: setDataManually,
  };
};

export default useFetchData;
